/**
* Library to manage system exceptions.
*
* @class flexygo.exceptions
*/


namespace flexygo.api.exceptions {
    /**
    * api for AjaxErrorData
    * @class AjaxErrorData
    * @constructor
    * @return {AjaxErrorData} .
    */
    export class AjaxErrorData {
        Title!: string;
        Message!: string;
        StackTrace!: string;
        InnerMessage!: string;
        Msgtype!: EajaxExceptionType;
    }

    export class UnhandledErrorData {
        StatusCode!: string;
        Title!: string;
        Message!: string;
        RawPayload?: string;
    }

    export class ApiError {
        Type!: ErrorType.Ajax | ErrorType.Message | ErrorType.Unhandled;
        Content!: AjaxErrorData | string | UnhandledErrorData;
    }

    export enum EajaxExceptionType {
        ErrorMsg = 0,
        SecurityMsg = 1,
        WarningMsg = 2
    }

    export enum ErrorType {
        Ajax = 'Ajax',
        Message = 'Message',
        Unhandled = 'Unhandled'
    }
}

namespace flexygo.exceptions {

    // Load exception styles if not already loaded
    (function loadStyles(): void {
        const styleId = 'flexygo-exceptions-styles';

        if (!document.getElementById(styleId)) {
            const link = document.createElement('link');
            link.id = styleId;
            link.rel = 'stylesheet';
            // Use a safe path: try flexygo.utils.webPath if available, otherwise use relative path from root
            const basePath = (typeof flexygo !== 'undefined' && flexygo.utils?.webPath) ? flexygo.utils.webPath : '';
            link.href = `${basePath}/css/exceptions/exceptions.css`;
            document.head.appendChild(link);
        }
    })();

    // --- SVG icon constants (self-contained, no external font dependency) ---
    const SVG_ICON_ERROR = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>';
    const SVG_ICON_LOCK = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';
    const SVG_ICON_WARNING = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
    const SVG_ICON_COPY = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';

    /** @global {object} lastError - Last exception catched.  */
    declare var lastError;

    /**
    * Launches a notification with catched exception info.
    * @method httpShow
    * @param {string} httpExc - Object with de catched exception.
    * @return {JQuery} [element] - Related element dialog.
    */
    export function httpShow(httpExc: any, element?: JQuery) {

        if (!httpExc.responseJSON) {
            var message = isBlank(httpExc.responseText) ? httpExc.statusText : JSON.stringify(httpExc.responseText);
            Lobibox.window({
                title: "Unknown Error",
                content: message
            });
            return;
        }

        var error = normalizeKeys<flexygo.api.exceptions.ApiError>(httpExc.responseJSON);

        handleAndShowError(error, element);
    }

    /**
     * Shows an ApiError in a consistent way.
     * - Ajax: structured payload (object) -> httpShow.responseJSON
     * - Message: user-facing text -> toast
     * - Unhandled: technical text -> httpShow.responseText
     */
    export function handleAndShowError(error: flexygo.api.exceptions.ApiError, element?: JQuery): void {

        const normalized = normalizeKeys<flexygo.api.exceptions.ApiError>(error);
        const { Type, Content } = normalized;

        // Store last error for potential debugging
        lastError = normalized;

        switch (Type) {
            case flexygo.api.exceptions.ErrorType.Ajax:
                handleAjaxError(normalizeKeys<flexygo.api.exceptions.AjaxErrorData>(Content), element);
                break;

            case flexygo.api.exceptions.ErrorType.Message:
                handleMessageError(Content as string, element);
                break;

            case flexygo.api.exceptions.ErrorType.Unhandled:
                handleUnhandledError(normalizeKeys<flexygo.api.exceptions.UnhandledErrorData>(Content), element);
                break;

            default:
                // Fallback for unknown error types
                Lobibox.window({
                    title: "Unknown Error",
                    content: JSON.stringify(Content)
                });
                break;
        }
    }

    /**
     * Shows the full error details in a Lobibox window.
     * @param error Optional ApiError to display. If omitted, uses the last captured error.
     */
    export function showFullError(error?: flexygo.api.exceptions.ApiError): void {
        const target = error ?? lastError;
        if (!target) {
            flexygo.msg.warning('No error details available.');
            return;
        }

        // Store the provided error so follow-up calls without args work too
        if (error) lastError = error;

        const normalized = normalizeKeys<flexygo.api.exceptions.ApiError>(target);
        const { Type, Content } = normalized;

        switch (Type) {
            case flexygo.api.exceptions.ErrorType.Ajax:
                const ajaxData = normalizeKeys<flexygo.api.exceptions.AjaxErrorData>(Content);
                ShowFullAjaxError(ajaxData);
                break;

            case flexygo.api.exceptions.ErrorType.Unhandled:
                const unhandledData = normalizeKeys<flexygo.api.exceptions.UnhandledErrorData>(Content);
                ShowUnhandledError(unhandledData);
                break;

            case flexygo.api.exceptions.ErrorType.Message:
                const errorMessage = Content as string;
                ShowFullMessageError(errorMessage);
                break;
        }
    }

    /**
     * Copies text to clipboard and shows a notification.
     */
    export function copyToClipboard(text: string): void {
        // Decode HTML entities
        let decodedText = text
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&#44;/g, ',')
            .replace(/&#58;/g, ':')
            .replace(/&#124;/g, '|');

        flexygo.utils.copyClipboard(decodedText);
    }

    /**
     * Handles Ajax errors with structured data.
     */
    function handleAjaxError(data: flexygo.api.exceptions.AjaxErrorData, element?: JQuery): void {
        const { Title, Message, StackTrace, InnerMessage, Msgtype } = data;

        // Determine if we have additional details to show
        const hasDetails = !flexygo.utils.isBlank(StackTrace) || !flexygo.utils.isBlank(InnerMessage);

        // Build clickable message if there are details
        let displayMessage = Message;
        let displayTitle = Title;

        if (!element && hasDetails) {
            displayMessage = buildClickableMessage(Message);
            displayTitle = buildClickableTitle(Title);
        }

        // Show notification based on message type
        switch (Msgtype) {
            case flexygo.api.exceptions.EajaxExceptionType.SecurityMsg:
            case flexygo.api.exceptions.EajaxExceptionType.WarningMsg:
                flexygo.msg.warning(displayMessage, element, displayTitle, element ? 'center top' : undefined);
                break;

            case flexygo.api.exceptions.EajaxExceptionType.ErrorMsg:
            default:
                flexygo.msg.error(displayMessage, element, displayTitle);
                break;
        }
    }

    /**
     * Handles simple message errors (user-facing text).
     */
    function handleMessageError(message: string, element?: JQuery): void {
        const displayMessage = buildClickableMessage(message);
        const displayTitle = buildClickableTitle('Error');

        flexygo.msg.error(displayMessage, element, displayTitle);
    }

    /**
     * Handles unhandled errors with status code and raw payload.
     */
    function handleUnhandledError(data: flexygo.api.exceptions.UnhandledErrorData, element?: JQuery): void {
        const { StatusCode, Title, Message, RawPayload } = data;

        let displayMessage = Message || "An unexpected error occurred.";
        let displayTitle = Title || `Error ${StatusCode}`;

        if (!element && RawPayload) {
            displayMessage = buildClickableMessage(displayMessage);
            displayTitle = buildClickableTitle(displayTitle);
        }

        flexygo.msg.error(displayMessage, element, displayTitle);
    }

    /**
     * Builds a clickable HTML message that opens the full error details.
     */
    function buildClickableMessage(text: string): string {
        return `<span onclick="flexygo.exceptions.showFullError();" style="cursor: pointer;">${text}<br/><u>More info</u></span>`;
    }

    /**
     * Builds a clickable HTML title that opens the full error details (without 'More info' text).
     */
    function buildClickableTitle(text: string): string {
        return `<span onclick="flexygo.exceptions.showFullError();" style="cursor: pointer;">${text}</span>`;
    }

    /**
     * Builds the error header HTML with icon, title and badge.
     */
    function buildErrorHeader(title: string, headerClass: string = 'flx-exception-header', iconSvg: string = SVG_ICON_ERROR): string {
        return `
        <div class="${headerClass}">
            <div class="flx-exception-header__icon">
                ${iconSvg}
            </div>

            <h1 class="flx-exception-header__title">
                ${title}
            </h1>
        </div>`;
    }

    /**
     * Shows full Ajax error details with appropriate badge based on message type.
     */
    function ShowFullAjaxError(data: flexygo.api.exceptions.AjaxErrorData): void {
        const { Title, Message, InnerMessage, StackTrace, Msgtype } = data;

        let iconSvg: string;

        switch (Msgtype) {
            case flexygo.api.exceptions.EajaxExceptionType.SecurityMsg:
                iconSvg = SVG_ICON_LOCK;
                break;

            case flexygo.api.exceptions.EajaxExceptionType.WarningMsg:
                iconSvg = SVG_ICON_WARNING;
                break;

            case flexygo.api.exceptions.EajaxExceptionType.ErrorMsg:
            default:
                iconSvg = SVG_ICON_ERROR;
                break;
        }

        // Determine message class based on type
        const isWarningType = Msgtype === flexygo.api.exceptions.EajaxExceptionType.SecurityMsg || Msgtype === flexygo.api.exceptions.EajaxExceptionType.WarningMsg;

        const headerClass = isWarningType ? 'flx-exception-header flx-exception-header--warning' : 'flx-exception-header';

        const header = buildErrorHeader(Title, headerClass, iconSvg);

        const messageClass = isWarningType ? 'flx-exception-body__message flx-exception-body__message--warning' : 'flx-exception-body__message';

        let body = `<div class="flx-exception-body">`;

        // Main message
        if (Message) {
            body += `
            <div class="${messageClass}">
                <b>Message: </b>${Message}
            </div>`;
        }

        // Additional data section
        const hasAdditionalData = !flexygo.utils.isBlank(InnerMessage) || !flexygo.utils.isBlank(StackTrace);
        if (hasAdditionalData) {
            body += `<div class="flx-exception-body__additional-data">`;

            if (InnerMessage) {
                const escapedInnerMessage = escapeHtmlString(InnerMessage);
                body += `
                    <div class="flx-exception-body__data-item">
                        <div class="flx-exception-body__data-header">
                            <div class="flx-exception-body__data-label">Inner Message</div>
                            <span class="flx-exception-body__copy-btn" 
                               onclick="flexygo.exceptions.copyToClipboard(\`${escapeJsString(escapedInnerMessage)}\`)" 
                               title="Copy to clipboard">${SVG_ICON_COPY}</span>
                        </div>
                        <div class="flx-exception-body__error-content">${InnerMessage}</div>
                    </div>`;
            }

            if (StackTrace) {
                const escapedStackTrace = escapeHtmlString(StackTrace);
                body += `
                    <div class="flx-exception-body__data-item">
                        <div class="flx-exception-body__data-header">
                            <div class="flx-exception-body__data-label">Stack Trace</div>
                            <span class="flx-exception-body__copy-btn" 
                               onclick="flexygo.exceptions.copyToClipboard(\`${escapeJsString(escapedStackTrace)}\`)"
                               title="Copy to clipboard">${SVG_ICON_COPY}</span>
                        </div>
                        <div class="flx-exception-body__stacktrace">
                            <code>${replaceAll(StackTrace, "\r\n", "<br>")}</code>
                        </div>
                    </div>`;
            }

            body += `</div>`;
        }

        body += `</div>`;

        Lobibox.window({ title: header, content: body });
    }

    /**
     * Shows full message error details.
     */
    function ShowFullMessageError(message: string): void {
        const header = buildErrorHeader('Error');

        const body = `
        <div class="flx-exception-body">
            <div class="flx-exception-body__message">
                <b>Message: </b>${message}
            </div>
        </div>`;

        Lobibox.window({ title: header, content: body });
    }

    /**
     * Shows full unhandled error details.
     */
    function ShowUnhandledError(data: flexygo.api.exceptions.UnhandledErrorData): void {
        const { Title, StatusCode, Message, RawPayload } = data;

        const header = buildErrorHeader(Title);

        let body = `<div class="flx-exception-body">`;

        // Main message
        if (Message) {
            body += `
            <div class="flx-exception-body__message">
                <b>Message: </b>${Message}
            </div>`;
        }

        // Additional data section
        const hasAdditionalData = !flexygo.utils.isBlank(StatusCode) || !flexygo.utils.isBlank(RawPayload);
        if (hasAdditionalData) {
            body += `<div class="flx-exception-body__additional-data">`;

            if (StatusCode) {
                const escapedStatusCode = escapeHtmlString(StatusCode);
                body += `
                    <div class="flx-exception-body__data-item">
                        <div class="flx-exception-body__data-header">
                            <div class="flx-exception-body__data-label">Status Code</div>
                            <span class="flx-exception-body__copy-btn" 
                               onclick="flexygo.exceptions.copyToClipboard(\`${escapeJsString(escapedStatusCode)}\`)"
                               title="Copy to clipboard">${SVG_ICON_COPY}</span>
                        </div>
                        <div class="flx-exception-body__error-content">${StatusCode}</div>
                    </div>`;
            }

            if (RawPayload) {

                // Format RawPayload: if it's a string, try to parse and pretty-print it, otherwise stringify it
                let formattedPayload: string;
                try {
                    if (typeof RawPayload === 'string') {
                        // Try to parse as JSON and pretty-print
                        const parsed = JSON.parse(RawPayload);
                        formattedPayload = JSON.stringify(parsed, null, 2);
                    } else {
                        // It's already an object, stringify it
                        formattedPayload = JSON.stringify(RawPayload, null, 2);
                    }
                } catch {
                    // If parsing fails, use as-is
                    formattedPayload = RawPayload.toString();
                }

                const escapedPayload = escapeHtmlString(formattedPayload);
                body += `
                    <div class="flx-exception-body__data-item">
                        <div class="flx-exception-body__data-header">
                            <div class="flx-exception-body__data-label">Raw Payload</div>
                            <span class="flx-exception-body__copy-btn" 
                               onclick="flexygo.exceptions.copyToClipboard(\`${escapeJsString(escapedPayload)}\`)"
                               title="Copy to clipboard">${SVG_ICON_COPY}</span>
                        </div>
                        <pre class="flx-exception-body__raw-payload">${escapedPayload}</pre>
                    </div>`;
            }

            body += `</div>`;
        }

        body += `</div>`;

        Lobibox.window({ title: header, content: body });
    }

    function escapeHtmlString(str: string) {
        if (str) {
            str = replaceAll(str, '&', '&amp;');
            str = replaceAll(str, '<', '&lt;');
            str = replaceAll(str, '>', '&gt;');
            str = replaceAll(str, '"', '&quot;');
            str = replaceAll(str, "'", '&#39;');
            str = replaceAll(str, ',', '&#44;')
            str = replaceAll(str, ':', '&#58;')
            str = replaceAll(str, '|', '&#124;')
        }
        return str;
    }

    function escapeJsString(str: string) {
        if (str) {
            str = replaceAll(str, '\\', '\\\\');
            str = replaceAll(str, '\'', '\\\'');
            str = replaceAll(str, '`', '\\`');
            str = replaceAll(str, '${', '\\${');
        }

        return str;
    }

    /**
     * Normalizes an object's keys to PascalCase so that server responses
     * with camelCase properties (e.g. statusCode) match the expected
     * PascalCase model definitions (e.g. StatusCode).
     */
    function normalizeKeys<T>(obj: any): T {
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
        const normalized: any = {};
        for (const key of Object.keys(obj)) {
            const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
            normalized[pascalKey] = obj[key];
        }
        return normalized as T;
    }

    function replaceAll(str, find, replace) {
        if (!str || find == replace) {
            return str;
        }

        let escapeRegExp = function (str) {
            let callee: any = arguments.callee;

            if (!callee.sRE) {
                let specials = [
                    '/', '.', '*', '+', '?', '|',
                    '(', ')', '[', ']', '{', '}', '\\', '$'
                ];
                callee.sRE = new RegExp(
                    '(\\' + specials.join('|\\') + ')', 'gim'
                );
            }
            return str.replace(callee.sRE, '\\$1');
        }

        if (!flexygo.utils.isBlank(replace)) {
            replace = <any>replace.toString().replace(/$&/g, '&dollar;&amp;')
        }

        return str.toString().replace(new RegExp(escapeRegExp(find), 'ig'), replace);
    }

    function isBlank(value: any): boolean {

        if (typeof value === 'undefined') {
            return true
        } else if (value == null) {
            return true
        } else if (value.toString().trim() === '') {
            return true
        } else {
            return false
        }
    }
}